import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  Search, 
  Filter,
  Phone,
  Mail,
  Eye,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const LeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [filterStatus]);

  const fetchLeads = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const data = await apiClient.get(`/api/leads/${params}`);
      setLeads(data);
    } catch (error) {
      toast.error('Грешка при зареждане на лидове');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await apiClient.patch(`/api/leads/${leadId}/status?status=${newStatus}`);
      toast.success('Статус обновен');
      fetchLeads();
    } catch (error) {
      toast.error('Грешка при обновяване');
    }
  };

  const updateLeadPriority = async (leadId, newPriority) => {
    try {
      await apiClient.patch(`/api/leads/${leadId}/priority?priority=${newPriority}`);
      toast.success('Приоритет обновен');
      fetchLeads();
    } catch (error) {
      toast.error('Грешка при обновяване');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този лийд?')) return;
    
    try {
      await apiClient.delete(`/api/leads/${leadId}`);
      toast.success('Лийд изтрит');
      fetchLeads();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.phone.includes(searchTerm) ||
    (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const statusBadgeColor = (status) => {
    const colors = {
      'new': 'bg-[#D4AF37]/20 text-[#D4AF37]',
      'contacted': 'bg-blue-500/20 text-blue-400',
      'qualified': 'bg-green-500/20 text-green-400',
      'converted': 'bg-purple-500/20 text-purple-400',
      'closed': 'bg-gray-500/20 text-gray-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const priorityBadgeColor = (priority) => {
    const colors = {
      'high': 'bg-red-500/20 text-red-400',
      'medium': 'bg-yellow-500/20 text-yellow-400',
      'low': 'bg-green-500/20 text-green-400'
    };
    return colors[priority] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8] mb-2">
            Лидове
          </h1>
          <p className="text-[#A9A9A9]">
            Управление на запитвания и лидове
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-[#A9A9A9]">
            Общо: <span className="text-[#D4AF37] font-semibold">{leads.length}</span>
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A9A9A9]" />
            <Input
              type="text"
              placeholder="Търсене по име, телефон, имейл..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8]"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#0B0B0B] border border-[#D4AF37]/20 text-[#F6F2E8]"
          >
            <option value="all">Всички статуси</option>
            <option value="new">Нови</option>
            <option value="contacted">Контактирани</option>
            <option value="qualified">Квалифицирани</option>
            <option value="converted">Конвертирани</option>
            <option value="closed">Затворени</option>
          </select>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0B0B0B] border-b border-[#D4AF37]/10">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Име</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Контакт</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Интерес</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Тип</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Статус</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Приоритет</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#D4AF37] uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D4AF37]/5">
              {filteredLeads.map((lead) => (
                <tr key={lead._id} className="hover:bg-[#D4AF37]/5 transition-colors">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-[#F6F2E8]">{lead.name}</p>
                    <p className="text-xs text-[#A9A9A9]">{new Date(lead.created_at).toLocaleDateString('bg-BG')}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <a href={`tel:${lead.phone}`} className="flex items-center space-x-2 text-sm text-[#D4AF37] hover:underline">
                        <Phone size={14} />
                        <span>{lead.phone}</span>
                      </a>
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center space-x-2 text-sm text-[#A9A9A9] hover:text-[#D4AF37]">
                          <Mail size={14} />
                          <span>{lead.email}</span>
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-[#F6F2E8]">{lead.interest}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-[#A9A9A9] capitalize">{lead.client_type}</span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded ${statusBadgeColor(lead.status)} bg-transparent border-0 cursor-pointer`}
                    >
                      <option value="new">Нов</option>
                      <option value="contacted">Контактиран</option>
                      <option value="qualified">Квалифициран</option>
                      <option value="converted">Конвертиран</option>
                      <option value="closed">Затворен</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={lead.priority}
                      onChange={(e) => updateLeadPriority(lead._id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded ${priorityBadgeColor(lead.priority)} bg-transparent border-0 cursor-pointer`}
                    >
                      <option value="low">Нисък</option>
                      <option value="medium">Среден</option>
                      <option value="high">Висок</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded transition-colors"
                        title="Преглед"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => deleteLead(lead._id)}
                        className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-colors"
                        title="Изтрий"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-serif font-semibold text-[#F6F2E8]">
                  Детайли за лийд
                </h2>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-[#A9A9A9] hover:text-[#D4AF37]"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#A9A9A9] mb-1">Име</p>
                  <p className="text-lg font-medium text-[#F6F2E8]">{selectedLead.name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#A9A9A9] mb-1">Телефон</p>
                    <a href={`tel:${selectedLead.phone}`} className="text-[#D4AF37] hover:underline">
                      {selectedLead.phone}
                    </a>
                  </div>
                  {selectedLead.email && (
                    <div>
                      <p className="text-xs text-[#A9A9A9] mb-1">Имейл</p>
                      <a href={`mailto:${selectedLead.email}`} className="text-[#D4AF37] hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs text-[#A9A9A9] mb-1">Интерес</p>
                  <p className="text-[#F6F2E8]">{selectedLead.interest}</p>
                </div>

                <div>
                  <p className="text-xs text-[#A9A9A9] mb-1">Тип клиент</p>
                  <p className="text-[#F6F2E8] capitalize">{selectedLead.client_type}</p>
                </div>

                {selectedLead.message && (
                  <div>
                    <p className="text-xs text-[#A9A9A9] mb-1">Съобщение</p>
                    <p className="text-[#F6F2E8] bg-[#0B0B0B] p-3 rounded">
                      {selectedLead.message}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#A9A9A9] mb-1">Статус</p>
                    <span className={`text-xs px-2 py-1 rounded ${statusBadgeColor(selectedLead.status)}`}>
                      {selectedLead.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-[#A9A9A9] mb-1">Приоритет</p>
                    <span className={`text-xs px-2 py-1 rounded ${priorityBadgeColor(selectedLead.priority)}`}>
                      {selectedLead.priority}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-[#A9A9A9] mb-1">Източник</p>
                    <p className="text-[#F6F2E8]">{selectedLead.source}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
